/**
 * OutputValidation — the agent-run output-validation panel
 * (frontend-entity-coverage §2.3: AgentRun → output-validation; the boxed-agent
 * Approve/Reject surface).
 *
 * The validation columns (`requires_human_review`, produced Claims/Evidence/
 * Events counts) are `[PROPOSED]` (`02 §3.3`, `09`). Until the validation API
 * ships this renders the §5.5 labeled-empty teaser plus the **Approve / Reject**
 * affordance as a disabled shell — present, discoverable, but not yet live (the
 * architecture law: render the panel + the Approve/Reject affordance, no live
 * wiring in this slice). When the run is still running the panel says so; when
 * it failed it points at the Error block; it never hard-errors a show page.
 */
import { Check, X } from "@/components/ui/icon";
import type { AgentRun } from "@aos/shared";

import { Button } from "@/components/ui/button";
import { ProposedShowTeaser } from "../_shared/ProposedModule";

export interface OutputValidationProps {
  run: AgentRun;
}

export function OutputValidation({ run }: OutputValidationProps) {
  const running = run.status === "running" || run.status === "queued";

  return (
    <div className="space-y-3" data-testid="agent-run-output-validation">
      {running ? (
        <p className="text-sm text-muted-foreground">
          Validation runs once the agent finishes.
        </p>
      ) : (
        <ProposedShowTeaser
          endpoint="/api/agent-runs/:id (validation columns)"
          note={
            <>
              Output validation (review-required flag, produced Claim / Evidence
              / Event counts) appears once the validation columns ship{" "}
              <span className="text-xs">([PROPOSED])</span>. Approve / Reject the
              run from here.
            </>
          }
        />
      )}

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled
          data-testid="agent-run-approve"
        >
          <Check className="mr-2 h-4 w-4" /> Approve
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled
          data-testid="agent-run-reject"
        >
          <X className="mr-2 h-4 w-4" /> Reject
        </Button>
        <span className="text-xs text-muted-foreground">
          Wired with the Assistant slice.
        </span>
      </div>
    </div>
  );
}

export default OutputValidation;
